import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getDataSources } from "../../../services/dataLinage.service";
import { promoteInternalSilverAssetToRdv } from "../../../services/dataVaultModelling.service";
import {
  SearchableSelect,
  type SearchableSelectOption
} from "../../../components/SearchableSelect/SearchableSelect";

type SilverAssetOption = {
  data_source_id: number;
  data_source_name: string;
  source_entity_id: number;
  source_entity_name: string;
  source_entity_description: string | null;
  source_schema: string | null;
};

const isInternalRegisteredSource = (value?: string | null) =>
  (value ?? "").trim().toUpperCase() === "INTERNAL_REGISTERED";
const isSilverLayerEntity = (value?: string | null) =>
  (value ?? "").trim().toLowerCase() === "silver";

const pickerInputClass =
  "tw-mt-1 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300";

const SUBJECT_AREA_DATALIST_ID = "promote-rdv-subject-area-options";

export function PromoteRdvAssetModal({
  open,
  onClose,
  onPromoted,
  subjectAreaOptions = [],
  hubOptions = [],
}: {
  open: boolean;
  onClose: () => void;
  onPromoted: () => void;
  // Existing subject areas in the model — offered as a dropdown; the field still accepts a new value.
  subjectAreaOptions?: string[];
  // Existing hubs in the model — required as the parent when promoting a satellite.
  hubOptions?: Array<{ id: string; name: string }>;
}): JSX.Element | null {
  const [assets, setAssets] = useState<SilverAssetOption[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [tableConstruct, setTableConstruct] = useState("HubAsTable");
  const [parentHubId, setParentHubId] = useState("");
  const [subjectArea, setSubjectArea] = useState("");
  const [ensemble, setEnsemble] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const assetSelectOptions = useMemo<SearchableSelectOption[]>(
    () =>
      assets.map((a) => ({
        label: `${a.source_entity_name}${a.data_source_name ? ` (${a.data_source_name})` : ""}`,
        value: String(a.source_entity_id),
        keywords: a.source_schema ?? undefined
      })),
    [assets]
  );
  const hubSelectOptions = useMemo<SearchableSelectOption[]>(
    () => hubOptions.map((h) => ({ label: h.name, value: h.id })),
    [hubOptions]
  );

  const loadAssets = useCallback(async () => {
    setLoadingAssets(true);
    setLoadError(null);
    try {
      const response = await getDataSources();
      const records = Array.isArray(response) ? response : [];
      const options = (records as any[])
        .filter((ds) => isInternalRegisteredSource(ds?.data_source_type))
        .flatMap((ds) =>
          (Array.isArray(ds?.source_entity) ? ds.source_entity : [])
            .filter((e: any) => isSilverLayerEntity(e?.source_entity_layer))
            .map((e: any) => ({
              data_source_id: ds.data_source_id,
              data_source_name: ds.data_source_name,
              source_entity_id: e.source_entity_id,
              source_entity_name: e.source_entity_name,
              source_entity_description: e.source_entity_description ?? null,
              source_schema: e.source_schema ?? null,
            }))
        )
        .sort((a: SilverAssetOption, b: SilverAssetOption) =>
          a.source_entity_name.localeCompare(b.source_entity_name)
        );
      setAssets(options);
    } catch (error) {
      console.error("Unable to load internal silver assets", error);
      setAssets([]);
      setLoadError("Unable to load custom Data Vault source entities.");
    } finally {
      setLoadingAssets(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      // reset + (re)load each time it opens
      setSelectedAssetId("");
      setTableConstruct("HubAsTable");
      setParentHubId("");
      setSubjectArea("");
      setEnsemble("");
      setSubmitError(null);
      setSubmitting(false); // ensure a prior promote can't leave the button stuck on "Promoting…"
      void loadAssets();
    }
  }, [open, loadAssets]);

  const selectedAsset = useMemo(
    () => assets.find((a) => String(a.source_entity_id) === selectedAssetId) ?? null,
    [assets, selectedAssetId]
  );

  const isSatellite = tableConstruct === "SatAsTable";
  const canSubmit =
    !!selectedAsset && (!isSatellite || !!parentHubId) && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!selectedAsset) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await promoteInternalSilverAssetToRdv({
        source_entity_id: selectedAsset.source_entity_id,
        table_construct: tableConstruct,
        parent_hub_target_entity_rdv_id:
          isSatellite && parentHubId ? Number(parentHubId) : null,
        subject_area: subjectArea.trim() || null,
        ensemble: ensemble.trim() || null,
      });
      onPromoted();
      onClose();
    } catch (error: any) {
      console.error("Promote to RDV failed", error);
      const status = error?.response?.status ?? error?.status;
      const detail = error?.response?.data?.detail ?? error?.data?.detail;
      setSubmitError(
        status === 409
          ? "This entity has already been promoted to the Raw Data Vault."
          : typeof detail === "string"
          ? detail
          : "Could not promote the entity. Please try again."
      );
    } finally {
      // Always clear the loading flag so the button can never get stuck on "Promoting…".
      setSubmitting(false);
    }
  }, [selectedAsset, tableConstruct, isSatellite, parentHubId, subjectArea, ensemble, onPromoted, onClose]);

  if (!open) {
    return null;
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-z-[1900] tw-flex tw-items-center tw-justify-center tw-bg-slate-900/40 tw-backdrop-blur-sm">
      <div className="tw-mx-4 tw-w-full tw-max-w-lg tw-rounded-3xl tw-border tw-border-slate-200 tw-bg-white tw-p-6 tw-shadow-2xl">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-400">
              Data Catalog
            </p>
            <h2 className="tw-text-lg tw-font-semibold tw-text-slate-900">
              Promote custom entity to Raw Data Vault
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-sm tw-text-slate-600 hover:tw-bg-slate-100"
          >
            Close
          </button>
        </div>

        <div className="tw-mt-4 tw-space-y-4">
          <div>
            <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
              Custom Data Vault source entity
            </label>
            <SearchableSelect
              value={selectedAssetId}
              options={assetSelectOptions}
              onChange={setSelectedAssetId}
              disabled={loadingAssets}
              placeholder={loadingAssets ? "Loading…" : "Select a custom silver entity"}
              aria-label="Custom Data Vault source entity"
              inputClassName={pickerInputClass}
            />
            {loadError ? (
              <p className="tw-mt-1 tw-text-xs tw-text-rose-600">{loadError}</p>
            ) : null}
            {!loadingAssets && !loadError && assets.length === 0 ? (
              <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                No custom silver source entities found. Register one in the Data Catalog first.
              </p>
            ) : null}
          </div>

          <div>
            <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
              Construct
            </label>
            <select
              value={tableConstruct}
              onChange={(e) => setTableConstruct(e.target.value)}
              className="tw-mt-1 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
            >
              <option value="HubAsTable">Hub</option>
              <option value="SatAsTable">Satellite</option>
            </select>
          </div>

          {isSatellite ? (
            <div>
              <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                Parent hub
              </label>
              <SearchableSelect
                value={parentHubId}
                options={hubSelectOptions}
                onChange={setParentHubId}
                placeholder={hubOptions.length ? "Select a parent hub" : "No hubs available"}
                aria-label="Parent hub"
                inputClassName={pickerInputClass}
              />
              {hubOptions.length === 0 ? (
                <p className="tw-mt-1 tw-text-xs tw-text-slate-500">
                  A satellite must attach to an existing hub. Add or promote a hub first.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <div>
              <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                Subject area
              </label>
              <input
                list={SUBJECT_AREA_DATALIST_ID}
                value={subjectArea}
                onChange={(e) => setSubjectArea(e.target.value)}
                placeholder="select or type new"
                className="tw-mt-1 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
              />
              <datalist id={SUBJECT_AREA_DATALIST_ID}>
                {subjectAreaOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="tw-text-xs tw-font-semibold tw-uppercase tw-text-slate-500">
                Ensemble
              </label>
              <input
                value={ensemble}
                onChange={(e) => setEnsemble(e.target.value)}
                placeholder="optional"
                className="tw-mt-1 tw-w-full tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-text-slate-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-slate-300"
              />
            </div>
          </div>

          {selectedAsset ? (
            <p className="tw-text-xs tw-text-slate-500">
              A new <strong>{isSatellite ? "Satellite" : "Hub"}</strong> named “
              {selectedAsset.source_entity_name}” will be created in the Raw Data Vault, linked to
              this catalog entity{isSatellite ? " and attached to the selected parent hub" : ""}.
              Columns are not seeded — model the {isSatellite ? "satellite’s attributes" : "hub’s business key and attributes"} on
              the canvas after promoting.
            </p>
          ) : null}

          {submitError ? (
            <p className="tw-rounded-2xl tw-bg-rose-50 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-rose-700">
              {submitError}
            </p>
          ) : null}

          <div className="tw-flex tw-justify-end tw-gap-2">
            <button
              type="button"
              onClick={onClose}
              className="tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="tw-rounded-full tw-border tw-border-[#03234a] tw-bg-[#03234a] tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-[#021b39] disabled:tw-cursor-not-allowed disabled:tw-opacity-60"
            >
              {submitting ? "Promoting…" : "Promote to RDV"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromoteRdvAssetModal;
